// =============================================================================
// uniquePagesBulkRunner — worker pool do generacji wielu podstron naraz.
// Wzorzec ze standaloneBulkRunner/schemaBackfillRunner. Concurrency 1-3.
// =============================================================================

import { generateSubpage } from "./subpageGenerator";

export interface UniqueBulkControl {
  isPaused: () => boolean;
  isStopRequested: () => boolean;
}

export interface UniqueBulkProgress {
  current: number;
  total: number;
  ok: number;
  skipped: number;
  failed: number;
  activeLabels: string[];
  phase: "running" | "paused" | "stopping" | "done";
}

export interface UniqueBulkSummary {
  total: number;
  ok: number;
  skipped: number;
  failed: number;
  durationSec: number;
  stopped: boolean;
  failures: Array<{ label: string; reason: string }>;
}

export interface UniqueBulkItem {
  subpageId: string;
  label: string;
}

const MAX_CONCURRENCY = 3;

export async function runUniquePagesBulk(
  items: UniqueBulkItem[],
  control: UniqueBulkControl,
  onProgress: (p: UniqueBulkProgress) => void,
  startedAt: number,
  concurrency = 2,
): Promise<UniqueBulkSummary> {
  const total = items.length;
  const conc = Math.max(1, Math.min(MAX_CONCURRENCY, concurrency));
  let idx = 0;
  let ok = 0, skipped = 0, failed = 0, processed = 0;
  let stopped = false;
  const active = new Set<string>();
  const failures: Array<{ label: string; reason: string }> = [];

  const emit = (phase: UniqueBulkProgress["phase"]) =>
    onProgress({ current: processed, total, ok, skipped, failed, activeLabels: Array.from(active), phase });

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const worker = async () => {
    while (true) {
      if (control.isStopRequested()) { stopped = true; return; }
      while (control.isPaused() && !control.isStopRequested()) { emit("paused"); await sleep(400); }
      if (control.isStopRequested()) { stopped = true; return; }
      const my = idx++;
      if (my >= total) return;
      const item = items[my];
      active.add(item.label);
      emit("running");
      try {
        const res = await generateSubpage(item.subpageId);
        if (res.status === "ok") ok++;
        else if (res.status === "skipped") skipped++;
        else { failed++; failures.push({ label: item.label, reason: res.reason || "błąd" }); }
      } catch (e) {
        failed++;
        failures.push({ label: item.label, reason: e instanceof Error ? e.message : "wyjątek" });
      } finally {
        active.delete(item.label);
        processed++;
        emit(control.isStopRequested() ? "stopping" : "running");
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(conc, total) }, () => worker()));
  emit("done");
  return { total, ok, skipped, failed, durationSec: Math.round((Date.now() - startedAt) / 1000), stopped, failures };
}
